import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { format, parseISO } from 'date-fns';
import { Check, Trash2, RefreshCw, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const CommentsManager = () => {
  const qc = useQueryClient();

  const { data: comments = [], isLoading } = useQuery({
    queryKey: ['admin_comments'],
    queryFn: async () => {
      const { data } = await supabase
        .from('blog_comments')
        .select('*')
        .order('is_approved', { ascending: true })
        .order('created_at', { ascending: false });
      return data || [];
    },
  });

  // Fetch post titles for display
  const { data: posts = [] } = useQuery({
    queryKey: ['admin_posts_titles'],
    queryFn: async () => {
      const { data } = await supabase.from('blog_posts').select('id, title_en, title_ro, slug');
      return data || [];
    },
  });

  const postMap = Object.fromEntries(posts.map(p => [p.id, p]));

  const approveMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('blog_comments').update({ is_approved: true }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin_comments'] }); toast.success('Comment approved'); },
    onError: (e: any) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('blog_comments').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin_comments'] }); toast.success('Comment deleted'); },
    onError: (e: any) => toast.error(e.message),
  });

  const regenerateReply = useMutation({
    mutationFn: async (comment: any) => {
      const post = postMap[comment.post_id];
      const { data, error } = await supabase.functions.invoke('ai-comment-reply', {
        body: {
          comment_content: comment.content,
          post_title: post?.title_en || 'Blog Post',
          post_excerpt: '',
          language: 'en',
        },
      });
      if (error) throw error;
      const { error: updateErr } = await supabase.from('blog_comments').update({ ai_reply: data.reply }).eq('id', comment.id);
      if (updateErr) throw updateErr;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin_comments'] }); toast.success('AI reply regenerated'); },
    onError: (e: any) => toast.error(e.message),
  });

  const pending = comments.filter(c => !c.is_approved);
  const approved = comments.filter(c => c.is_approved);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold font-fraunces" style={{ color: 'var(--blue)' }}>Comments</h1>
        <p className="text-muted-foreground text-sm mt-1">{pending.length} pending · {approved.length} approved</p>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground animate-pulse">Loading comments…</div>
      ) : comments.length === 0 ? (
        <Card className="border-border"><CardContent className="p-8 text-center text-muted-foreground">No comments yet</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {comments.map(c => {
            const post = postMap[c.post_id];
            return (
              <Card key={c.id} className="border-border">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-bold text-sm" style={{ color: 'var(--ink)' }}>{c.author_name}</span>
                        {c.author_email && <span className="text-xs text-muted-foreground">{c.author_email}</span>}
                        <Badge variant={c.is_approved ? 'default' : 'secondary'} className="text-[.6rem]">
                          {c.is_approved ? 'Approved' : 'Pending'}
                        </Badge>
                        <span className="text-xs text-muted-foreground">{format(parseISO(c.created_at), 'MMM dd, yyyy HH:mm')}</span>
                      </div>
                      {post && (
                        <p className="text-xs text-muted-foreground mb-2">
                          on <span className="font-medium" style={{ color: 'var(--blue)' }}>{post.title_en}</span>
                        </p>
                      )}
                      <p className="text-sm mb-3" style={{ color: 'var(--slate)' }}>{c.content}</p>

                      {c.ai_reply && (
                        <div className="pl-4 py-2 rounded" style={{ borderLeft: '3px solid var(--amber)', background: 'hsl(38 80% 55% / 0.05)' }}>
                          <span className="text-[.6rem] font-bold uppercase tracking-wider" style={{ color: 'var(--amber)', fontFamily: 'var(--f-mono)' }}>
                            <MessageCircle className="w-3 h-3 inline mr-1" />Alex's reply
                          </span>
                          <p className="text-sm mt-1" style={{ color: 'var(--slate)' }}>{c.ai_reply}</p>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-1.5 shrink-0">
                      {!c.is_approved && (
                        <Button size="sm" variant="outline" onClick={() => approveMutation.mutate(c.id)} disabled={approveMutation.isPending}>
                          <Check className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => regenerateReply.mutate(c)} disabled={regenerateReply.isPending}>
                        <RefreshCw className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-500 hover:text-red-700" onClick={() => deleteMutation.mutate(c.id)} disabled={deleteMutation.isPending}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CommentsManager;
